import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import AppBar from '@/karehero-app/shell/AppBar';
import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import { type CardIntro } from '@/karehero-app/architecture/Architecture.content';

// One of production's three icon lines.
const IntroItem = ({ icon, text }: { icon: string; text: string }) => (
  <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.5}>
    <Box sx={{ color: 'primary.main', display: 'flex', pt: '2px' }}>
      <Icon icon={icon as never} fontSize={16} />
    </Box>
    <Typography sx={{ flex: 1 }} variant='body2'>
      {text}
    </Typography>
  </Stack>
);

// PROPOSED. What a home card explains, as a full screen that is not a page.
//
// It was a sheet, and a sheet cannot hold Power of attorney: its four rows each
// open something to read, and a sheet over a sheet is a stack nobody can see
// the bottom of. So this is a screen — production's shape — presented by
// ModalScreen rather than routed.
//
// That distinction is the whole point. Routed, it would join the history, and
// somebody who read about the calculator, went to Care, then pressed back would
// land on an explanation of a thing they had already decided against. Presented,
// it is a layer: it opens over home, it closes, and nothing remembers it.
//
// An X rather than a chevron, and the two are not interchangeable: a chevron
// says there is something behind this that you came from, and it should slide
// in from the right to say so. This came up from the bottom and it dismisses,
// so it wears the mark for dismissing. Continue is the only way through, and
// where it goes is the caller's business.
const ArchitectureCardScreen = ({
  intro,
  onClose,
  onStart,
}: {
  intro: CardIntro;
  onClose: () => void;
  /** Continue, on the three cards that have a flow behind them. */
  onStart?: () => void;
}) => {
  return (
    <ScreenRoot relative>
      <StatusBar />
      <AppBar
        title={intro.title}
        align='left'
        size='small'
        action={
          <IconButton aria-label='Close' size='small' onClick={onClose}>
            <Icon icon={'xmark' as never} fontSize={18} />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, py: 2 }}>
        <Stack spacing={3}>
          <Typography variant='body1'>{intro.description}</Typography>

          {intro.items && (
            <Stack spacing={2}>
              {intro.items.map((item) => (
                <IntroItem key={item.text} icon={item.icon} text={item.text} />
              ))}
            </Stack>
          )}

          {/* Answered in place. Two of these are read together — what an LPA
              is, then why you would want one — and as rows opening sheets that
              was four taps to read two paragraphs. */}
          {intro.faqs && (
            <Box>
              {intro.faqs.map((faq) => (
                <Accordion key={faq.id}>
                  <AccordionSummary
                    expandIcon={<Icon icon='chevron-down' fontSize={14} />}
                  >
                    <Typography variant='h6'>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1.5}>
                      {faq.answer.map((paragraph) => (
                        <Typography key={paragraph} variant='body2'>
                          {paragraph}
                        </Typography>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Production's "Did you know?", kept because it is the only line
              doing persuasion rather than explanation. */}
          {intro.infoText && (
            <Card sx={{ p: 2, bgcolor: 'custom.limeGreen.100' }}>
              <Stack spacing={0.5}>
                <Typography variant='h6'>Did you know?</Typography>
                <Typography variant='body2'>{intro.infoText}</Typography>
              </Stack>
            </Card>
          )}
        </Stack>
      </Box>

      {intro.action && (
        <Box sx={{ px: 3, pt: 1.5, pb: 0.5, flexShrink: 0 }}>
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={intro.action}
            onPress={onStart ?? onClose}
          >
            {intro.action}
          </Button>
        </Box>
      )}

      <HomeIndicator />
    </ScreenRoot>
  );
};

export default ArchitectureCardScreen;
